/**
 * Hold to play (2026-10-09) — the Descent Board's launch button.
 *
 * The band puts mode and floor right beside the button, so a run can start
 * from the hub without the Play panel in between. A start that costs energy
 * (and maybe a contract charge) is then one stray tap away, so the button
 * asks for a short hold instead: the fill is the confirmation, and letting go
 * early is the cancel.
 *
 * Only while `isHoldable()` is true. When the run cannot start — no delver,
 * no energy, a mode not built yet — or when the button has become "Leave
 * queue", the press is left alone for the hub's own click handler, which
 * opens the Play panel to say why, or leaves the queue. A keyboard press
 * (Enter / Space arrive as a click with `detail === 0`) confirms at once:
 * holding a key is not a gesture anyone discovers.
 *
 * Progress is a `--hold` custom property from 0 to 1, drawn by app.css. A
 * quick tap sets `data-nudge` for a moment so the button shakes and shows
 * how far a hold has to go.
 */

export interface HoldToPlayOptions {
  holdMs: number;
  isHoldable: () => boolean;
  onConfirm: () => void;
}

const TAP_MS = 250;
const NUDGE_MS = 650;

export function bindHoldToPlay(btn: HTMLElement, opts: HoldToPlayOptions): () => void {
  let raf = 0;
  let startedAt = 0;
  let holding = false;
  /** Set when a hold completes: the click that follows the release must not reach the hub, which by then reads "Leave queue". */
  let swallowNext = false;
  let nudgeTimer: ReturnType<typeof setTimeout> | undefined;

  const setProgress = (p: number): void => { btn.style.setProperty('--hold', p.toFixed(3)); };

  function stop(): void {
    holding = false;
    cancelAnimationFrame(raf);
    btn.removeAttribute('data-holding');
    setProgress(0);
  }

  function frame(now: number): void {
    if (!holding) return;
    const p = Math.min(1, (now - startedAt) / opts.holdMs);
    setProgress(p);
    if (p >= 1) {
      stop();
      swallowNext = true;
      opts.onConfirm();
      return;
    }
    raf = requestAnimationFrame(frame);
  }

  function onDown(e: PointerEvent): void {
    if (e.button !== 0 || !opts.isHoldable()) return;
    e.preventDefault();
    btn.setPointerCapture?.(e.pointerId);
    holding = true;
    startedAt = performance.now();
    btn.setAttribute('data-holding', '');
    raf = requestAnimationFrame(frame);
  }

  function onRelease(): void {
    // The click that follows this release (if one comes at all) is the tail
    // of a hold that already fired; give it a moment to arrive, then forget.
    if (swallowNext) setTimeout(() => { swallowNext = false; }, 300);
    if (!holding) return;
    const quick = performance.now() - startedAt < TAP_MS;
    stop();
    if (quick) {
      clearTimeout(nudgeTimer);
      btn.removeAttribute('data-nudge');
      void btn.offsetWidth; // restart the shake on a second quick tap
      btn.setAttribute('data-nudge', '');
      nudgeTimer = setTimeout(() => btn.removeAttribute('data-nudge'), NUDGE_MS);
    }
  }

  // Capture phase, registered before the hub's own listener: while the run
  // can start, the click belongs to this gesture and must not also open the
  // panel; otherwise it is passed through untouched.
  function onClick(e: MouseEvent): void {
    if (swallowNext) { swallowNext = false; e.stopImmediatePropagation(); return; }
    if (!opts.isHoldable()) return;
    e.stopImmediatePropagation();
    if (e.detail === 0) opts.onConfirm();
  }

  btn.addEventListener('pointerdown', onDown);
  btn.addEventListener('pointerup', onRelease);
  btn.addEventListener('pointercancel', onRelease);
  btn.addEventListener('lostpointercapture', onRelease);
  btn.addEventListener('click', onClick, true);
  // A long press on a phone otherwise opens the text-selection callout.
  btn.addEventListener('contextmenu', (e) => { if (holding) e.preventDefault(); });

  return () => {
    stop();
    clearTimeout(nudgeTimer);
    btn.removeEventListener('pointerdown', onDown);
    btn.removeEventListener('pointerup', onRelease);
    btn.removeEventListener('pointercancel', onRelease);
    btn.removeEventListener('lostpointercapture', onRelease);
    btn.removeEventListener('click', onClick, true);
  };
}
